import { FormHelperText } from '@material-ui/core';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Body, Checkbox, Heading, Tooltip } from 'copilot-design-system';
import React from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import { FormBodyAPI } from 'src/constants';
import { BaseTextField } from 'src/legacy/components/TextField';
import { RootState } from 'src/store';
import { AddCreditCardForm } from 'src/store/payments/types';
import { StripePaymentMethod } from 'src/types/payment';
import { getCardLogo } from 'src/utils/BillingUtils';

import FixedCardNumberField from './FixedCardNumberField';

const validationScheme = Yup.object().shape({
  name: Yup.string().trim().required('Cardholder name is required'),
  postalCode: Yup.string().trim(),
});

export const useAddCreditCard = ({
  paymentMethods,
}: {
  paymentMethods: StripePaymentMethod[];
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { updatingPayment, updatingPaymentError: updatingError } = useSelector(
    (state: RootState) => state.payments,
  );

  const [errorMessage, setErrorMessage] = React.useState('');

  const cardError = errorMessage || updatingError;

  const getSubmitValues = async (values?: Partial<AddCreditCardForm>) => {
    if (!stripe || !elements || updatingPayment) {
      return { isValid: false };
    }

    if (values?.sourceId) {
      setErrorMessage('');
      return {};
    }

    const cardElement = elements.getElement(CardElement);

    if (cardElement) {
      try {
        const result = await stripe.createToken(cardElement, {
          name: values?.name,
          address_zip: values?.postalCode,
        });
        if (result.error) {
          setErrorMessage(result.error.message || 'Invalid card info.');
          return { isValid: false };
        }
        if (result.token) {
          return { token: result.token };
        }
      } catch (ex) {
        console.error('Error registering card', ex);
      }
    }

    return { isValid: false };
  };

  const FormRenderer: React.FC<FormBodyAPI> = ({
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
  }) => (
    <div>
      <div className="flex flex-col gap-3">
        <div>
          <Heading className="text-black-heading mb-1">Cardholder name</Heading>
          <BaseTextField
            sizeVariant="medium"
            error={Boolean(touched.name && errors.name)}
            fullWidth
            helperText={
              touched && errors && touched.name && errors.name
                ? errors.name
                : ''
            }
            name="name"
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.name}
            variant="outlined"
          />
        </div>

        <div className="w-full mb-2">
          <Heading className="text-black-heading mb-1">Card</Heading>
          {values?.sourceId ? (
            <FixedCardNumberField
              cardEnding={values.cardEnding}
              cardLogoPath={getCardLogo(values.brand)}
              expiration={`${values.expMonth}/${values.expYear}`}
            />
          ) : (
            <CardElement
              className="border border-solid border-non-hover-border rounded-4 py-2 px-3"
              options={{
                hidePostalCode: true,
              }}
            />
          )}
        </div>

        <div>
          <Heading className="text-black-heading mb-1">
            ZIP <span className="text-gray-small">(Optional)</span>
          </Heading>
          <BaseTextField
            sizeVariant="medium"
            error={Boolean(touched.postalCode && errors.postalCode)}
            value={values.postalCode}
            fullWidth
            label=""
            helperText={
              touched && errors && touched.postalCode && errors.postalCode
                ? errors.postalCode
                : ''
            }
            name="postalCode"
            variant="outlined"
            onBlur={handleBlur}
            onChange={handleChange}
          />
        </div>
        {((values?.sourceId && paymentMethods.length > 1) ||
          (!values?.sourceId && paymentMethods.length > 0)) && (
          <div className="flex">
            <Tooltip
              position="bottom"
              content={
                <Body className="text-sm text-black-heading">
                  Cannot unset default payment method
                </Body>
              }
              disabled={!values.isDefaultPaymentMethod}
            >
              <Checkbox
                label="Set as default payment method"
                checked={values.isPrimary}
                name="isPrimary"
                onChange={handleChange}
                disabled={values.isDefaultPaymentMethod}
              />
            </Tooltip>
          </div>
        )}
        {cardError && <FormHelperText error>{cardError}</FormHelperText>}
      </div>
    </div>
  );

  return { validationScheme, getSubmitValues, FormRenderer };
};
